/**
 * Pipeline data layer for the Command Center — lead + quote analytics derived
 * client-side from the existing `/Lead` and `/Quote` list endpoints.
 *
 * Mirrors the bucket logic the Quote Conversion Report already uses so the
 * dashboard and the report reconcile:
 *   Converted = WorkOrderGenerated
 *   Lost      = Rejected + Expired
 *   Open      = everything else still in play
 */
import * as React from 'react';

import { useLeadList } from '@/features/leads/api';
import { LeadStatus, leadStatusLabels } from '@/features/leads/schemas';
import { useQuoteList } from '@/features/quotes/api';
import { QuoteStatus } from '@/features/quotes/schemas';
import type { Quote } from '@/features/quotes/types';

/* -------------------------------------------------------------------------- */
/* Leads                                                                       */
/* -------------------------------------------------------------------------- */

export interface LeadStageDatum {
  key: number;
  label: string;
  count: number;
  /** Hex colour used for the bar fill. */
  color: string;
}

export interface LeadPipeline {
  isLoading: boolean;
  isError: boolean;
  total: number;
  /** Ordered progression stages (New → Open → Approved → Converted). */
  stages: LeadStageDatum[];
  converted: number;
  churned: number;
  cancelled: number;
  /** Converted ÷ all leads, as a percentage. */
  conversionRate: number;
}

const STAGE_COLORS: Record<number, string> = {
  [LeadStatus.New]: '#299cdb', // info
  [LeadStatus.Open]: '#3f86c4',
  [LeadStatus.Approved]: '#405189', // primary
  [LeadStatus.Converted]: '#0ab39c', // success
};

const STAGE_ORDER = [
  LeadStatus.New,
  LeadStatus.Open,
  LeadStatus.Approved,
  LeadStatus.Converted,
];

export function useLeadPipeline(): LeadPipeline {
  const q = useLeadList({ pageSize: 200 });

  return React.useMemo(() => {
    const leads = (q.data?.items ?? []).filter((l) => l && !l.isDeleted);
    const counts = new Map<number, number>();
    for (const l of leads) {
      counts.set(l.leadStatus, (counts.get(l.leadStatus) ?? 0) + 1);
    }
    const stages: LeadStageDatum[] = STAGE_ORDER.map((key) => ({
      key,
      label: leadStatusLabels[key] ?? 'Unknown',
      count: counts.get(key) ?? 0,
      color: STAGE_COLORS[key] ?? '#94a3b8',
    }));
    const total = leads.length;
    const converted = counts.get(LeadStatus.Converted) ?? 0;
    return {
      isLoading: q.isLoading,
      isError: q.isError,
      total,
      stages,
      converted,
      churned: counts.get(LeadStatus.Churned) ?? 0,
      cancelled: counts.get(LeadStatus.Cancelled) ?? 0,
      conversionRate: total > 0 ? (converted / total) * 100 : 0,
    };
  }, [q.data, q.isLoading, q.isError]);
}

/* -------------------------------------------------------------------------- */
/* Quotes                                                                      */
/* -------------------------------------------------------------------------- */

export interface QuoteOutcomeDatum {
  key: 'converted' | 'open' | 'lost';
  label: string;
  count: number;
  value: number;
  color: string;
}

export interface QuotePipeline {
  isLoading: boolean;
  isError: boolean;
  total: number;
  outcomes: QuoteOutcomeDatum[];
  /** Total $ value across all quotes in scope. */
  totalValue: number;
  /** $ value of open (in-play) quotes — the live pipeline. */
  pipelineValue: number;
  convertedValue: number;
  /** Converted ÷ all quotes, as a percentage. */
  winRate: number;
  /** Month-over-month deltas (undefined when prior month lacks signal). */
  pipelineValueDeltaPct?: number;
  winRateDeltaPts?: number;
}

const OUTCOME_COLORS = {
  converted: '#0ab39c',
  open: '#f7b84b',
  lost: '#f06548',
} as const;

const statusOf = (qt: Quote): number => Number(qt.status ?? QuoteStatus.Draft);
const valueOf = (qt: Quote): number => qt.total ?? qt.totalAmount ?? 0;

function isConverted(s: number): boolean {
  return s === QuoteStatus.WorkOrderGenerated;
}
function isLost(s: number): boolean {
  return s === QuoteStatus.Rejected || s === QuoteStatus.Expired;
}

/** `yyyy-MM` of a quote's quote-date, or '' when absent/invalid. */
function monthKey(iso: string | undefined | null): string {
  if (!iso) return '';
  return iso.slice(0, 7);
}

export function useQuotePipeline(): QuotePipeline {
  const q = useQuoteList({ pageSize: 200 });

  return React.useMemo(() => {
    const quotes = (q.data?.items ?? []).filter((qt) => qt?.id && !qt.isDeleted);

    let converted = 0;
    let convertedValue = 0;
    let open = 0;
    let openValue = 0;
    let lost = 0;
    let lostValue = 0;
    let totalValue = 0;

    for (const qt of quotes) {
      const s = statusOf(qt);
      const v = valueOf(qt);
      totalValue += v;
      if (isConverted(s)) {
        converted += 1;
        convertedValue += v;
      } else if (isLost(s)) {
        lost += 1;
        lostValue += v;
      } else {
        open += 1;
        openValue += v;
      }
    }

    const total = quotes.length;
    const winRate = total > 0 ? (converted / total) * 100 : 0;

    // Month-over-month deltas, gated on enough prior-month signal so small
    // datasets don't surface noisy swings.
    const now = new Date();
    const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const priorDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const priorMonth = `${priorDate.getFullYear()}-${String(priorDate.getMonth() + 1).padStart(2, '0')}`;

    let curValue = 0;
    let curTotal = 0;
    let curConv = 0;
    let priorValue = 0;
    let priorTotal = 0;
    let priorConv = 0;
    for (const qt of quotes) {
      const mk = monthKey(qt.quoteDate);
      const s = statusOf(qt);
      const v = valueOf(qt);
      if (mk === curMonth) {
        curValue += v;
        curTotal += 1;
        if (isConverted(s)) curConv += 1;
      } else if (mk === priorMonth) {
        priorValue += v;
        priorTotal += 1;
        if (isConverted(s)) priorConv += 1;
      }
    }

    let pipelineValueDeltaPct: number | undefined;
    let winRateDeltaPts: number | undefined;
    if (priorTotal >= 3) {
      if (priorValue > 0) {
        pipelineValueDeltaPct = ((curValue - priorValue) / priorValue) * 100;
      }
      const curWin = curTotal > 0 ? (curConv / curTotal) * 100 : 0;
      const priorWin = (priorConv / priorTotal) * 100;
      winRateDeltaPts = curWin - priorWin;
    }

    const outcomes: QuoteOutcomeDatum[] = [
      { key: 'converted', label: 'Converted', count: converted, value: convertedValue, color: OUTCOME_COLORS.converted },
      { key: 'open', label: 'Open', count: open, value: openValue, color: OUTCOME_COLORS.open },
      { key: 'lost', label: 'Lost', count: lost, value: lostValue, color: OUTCOME_COLORS.lost },
    ];

    return {
      isLoading: q.isLoading,
      isError: q.isError,
      total,
      outcomes,
      totalValue,
      pipelineValue: openValue,
      convertedValue,
      winRate,
      pipelineValueDeltaPct,
      winRateDeltaPts,
    };
  }, [q.data, q.isLoading, q.isError]);
}
